import {
    createContext,
    useCallback,
    useContext,
    useState,
    useRef
} from "react";
import type {
    DriveUploadProviderProps,
    UploadCompletionListener,
    DriveUploadContextType,
    UploadQueueItem
} from "./type";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize
} from "ncore-ui-kit";
import UploadWidget from "../../pages/drive/views/list/uploadWidget";
import RESTServiceContext from "../../services/restAPI/context";

export const DriveUploadContext = createContext<DriveUploadContextType | null>(null);

const CONCURRENCY = 3;

const DriveUploadProvider = ({
    children
}: DriveUploadProviderProps) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        uploadQueue,
        setUploadQueue
    ] = useState<UploadQueueItem[]>([]);

    const [
        isWidgetMinimized,
        setIsWidgetMinimized
    ] = useState<boolean>(false);

    const [
        isWidgetOpen,
        setIsWidgetOpen
    ] = useState<boolean>(false);

    const [
        folderName,
        setFolderName
    ] = useState<string>("Drive");

    const activeXhrsRef = useRef<Map<string, XMLHttpRequest>>(new Map());

    const listenersRef = useRef<Set<UploadCompletionListener>>(new Set());

    const isProcessingRef = useRef<boolean>(false);

    const queueRef = useRef<UploadQueueItem[]>([]);
    queueRef.current = uploadQueue;

    const updateQueueItem = useCallback((id: string, updates: Partial<UploadQueueItem>) => {
        setUploadQueue(prev => prev.map(item => {
            if(item.id === id) {
                return {
                    ...item,
                    ...updates
                };
            }
            return item;
        }));
    }, []);

    const uploadSingleItem = useCallback(async (item: UploadQueueItem): Promise<boolean> => {
        try {
            updateQueueItem(item.id, {
                status: "uploading",
                progress: 0
            });

            const tokenResult = await RESTServiceContext.action("getUploadTokenForDrive", {
                fileType: item.file.type || "application/octet-stream",
                folderID: item.folderID || undefined,
                fileSize: item.file.size,
                fileName: item.fileName,
                isPublic: true
            });

            const payload = tokenResult as unknown as { payload?: { token?: string; uploadToken?: string } };
            const token = payload.payload?.token || payload.payload?.uploadToken;

            if(!token) {
                updateQueueItem(item.id, {
                    errorMessage: "Token alınamadı",
                    status: "error"
                });
                return false;
            }

            await new Promise<void>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                activeXhrsRef.current.set(item.id, xhr);

                xhr.open("POST", `${process.env.STORAGE_URL}/upload`);
                xhr.setRequestHeader("Authorization", token);

                xhr.upload.onprogress = (event: ProgressEvent) => {
                    if(event.lengthComputable) {
                        const percent = Math.round((event.loaded / event.total) * 100);
                        updateQueueItem(item.id, {
                            progress: percent
                        });
                    }
                };

                xhr.onload = () => {
                    activeXhrsRef.current.delete(item.id);
                    if(xhr.status >= 200 && xhr.status < 300) {
                        resolve();
                    } else {
                        reject(new Error(`${xhr.status}`));
                    }
                };

                xhr.onerror = () => {
                    activeXhrsRef.current.delete(item.id);
                    reject(new Error("Network error"));
                };

                xhr.onabort = () => {
                    activeXhrsRef.current.delete(item.id);
                    reject(new Error("Aborted"));
                };

                const formData = new FormData();
                formData.append("file", item.file);
                xhr.send(formData);
            });

            const completedItem: UploadQueueItem = {
                ...item,
                status: "completed",
                progress: 100
            };

            updateQueueItem(item.id, {
                status: "completed",
                progress: 100
            });

            listenersRef.current.forEach(listener => {
                try {
                    listener(completedItem);
                } catch(error) {
                    console.error(error);
                }
            });

            return true;
        } catch(error) {
            activeXhrsRef.current.delete(item.id);
            console.error(error);
            updateQueueItem(item.id, {
                errorMessage: "Yükleme hatası",
                status: "error"
            });
            return false;
        }
    }, [
        updateQueueItem
    ]);

    const processQueue = useCallback(async () => {
        if(isProcessingRef.current) {
            return;
        }

        isProcessingRef.current = true;

        try {
            while(true) {
                const pendingItems = queueRef.current.filter(item => item.status === "pending");
                if(pendingItems.length === 0) {
                    break;
                }

                const batch = pendingItems.slice(0, CONCURRENCY);
                await Promise.all(batch.map(item => uploadSingleItem(item)));
            }

            const allDone = queueRef.current.every(item => item.status === "completed" || item.status === "error");
            if(allDone && queueRef.current.length > 0) {
                NCoreUIKitSnackBar.open({
                    title: localize("drive-upload-completed") || "Yükleme Tamamlandı"
                });
            }
        } finally {
            isProcessingRef.current = false;
        }
    }, [
        uploadSingleItem,
        localize
    ]);

    const uploadFiles = useCallback(async (files: File[], targetFolderID?: string) => {
        if(!files || files.length === 0) {
            return;
        }

        const newItems: UploadQueueItem[] = files.map((file, index) => ({
            id: `${Date.now()}-${index}-${file.name}`,
            folderID: targetFolderID,
            relativePath: file.name,
            fileName: file.name,
            status: "pending",
            folderPath: "",
            progress: 0,
            file
        }));

        setFolderName(files.length === 1 && files[0] ? files[0].name : `${files.length} dosya`);
        setIsWidgetMinimized(false);
        setIsWidgetOpen(true);

        setUploadQueue(prev => [
            ...prev,
            ...newItems
        ]);

        setTimeout(() => {
            processQueue();
        }, 100);
    }, [
        processQueue
    ]);

    const uploadFolder = useCallback(async (files: File[], parentFolderID?: string) => {
        if(!files || files.length === 0) {
            return;
        }

        const folderPathsSet = new Set<string>();
        let rootDirName = "Klasör";

        for(const file of files) {
            const relPath = ((file as unknown as { webkitRelativePath?: string }).webkitRelativePath || file.name).replace(/\\/g, "/");
            const parts = relPath.split("/").filter(Boolean);

            if(parts.length > 1) {
                rootDirName = parts[0] || "Klasör";
                let currentPath = "";
                for(let i = 0; i < parts.length - 1; i++) {
                    const part = parts[i];
                    if(part) {
                        currentPath = currentPath ? `${currentPath}/${part}` : part;
                        folderPathsSet.add(currentPath);
                    }
                }
            }
        }

        const paths = Array.from(folderPathsSet);
        let folderMap: Record<string, string> = {};

        if(paths.length > 0) {
            const hierarchyResult = await RESTServiceContext.action("batchCreateDriveFolderHierarchy", {
                parentFolderID: parentFolderID || undefined,
                paths
            });

            if(hierarchyResult.data && hierarchyResult.data.folderMap) {
                folderMap = hierarchyResult.data.folderMap;
            }
        }

        const newItems: UploadQueueItem[] = files.map((file, index) => {
            const relPath = ((file as unknown as { webkitRelativePath?: string }).webkitRelativePath || file.name).replace(/\\/g, "/");
            const parts = relPath.split("/").filter(Boolean);
            const folderPath = parts.length > 1 ? parts.slice(0, -1).join("/") : "";
            const targetFolderID = folderPath ? (folderMap[folderPath] || parentFolderID) : parentFolderID;

            return {
                id: `${Date.now()}-${index}-${file.name}`,
                folderID: targetFolderID,
                relativePath: relPath,
                fileName: file.name,
                status: "pending",
                progress: 0,
                folderPath,
                file
            };
        });

        setFolderName(rootDirName);
        setIsWidgetMinimized(false);
        setIsWidgetOpen(true);

        setUploadQueue(prev => [
            ...prev,
            ...newItems
        ]);

        setTimeout(() => {
            processQueue();
        }, 100);
    }, [
        processQueue
    ]);

    const cancelItem = useCallback((id: string) => {
        const xhr = activeXhrsRef.current.get(id);
        if(xhr) {
            xhr.abort();
            activeXhrsRef.current.delete(id);
        }
        updateQueueItem(id, {
            errorMessage: "İptal edildi",
            status: "error"
        });
    }, [
        updateQueueItem
    ]);

    const clearCompleted = useCallback(() => {
        setUploadQueue(prev => prev.filter(item => item.status !== "completed"));
    }, []);

    const subscribeToCompletion = useCallback((listener: UploadCompletionListener) => {
        listenersRef.current.add(listener);
        return () => {
            listenersRef.current.delete(listener);
        };
    }, []);

    return <DriveUploadContext.Provider
        value={{
            subscribeToCompletion,
            setIsWidgetMinimized,
            isWidgetMinimized,
            setIsWidgetOpen,
            clearCompleted,
            isWidgetOpen,
            uploadFolder,
            uploadFiles,
            uploadQueue,
            cancelItem,
            folderName
        }}
    >
        {children}
        <UploadWidget
            onToggleMinimize={() => setIsWidgetMinimized(prev => !prev)}
            onClose={() => setIsWidgetOpen(false)}
            isMinimized={isWidgetMinimized}
            folderName={folderName}
            isOpen={isWidgetOpen}
            items={uploadQueue}
        />
    </DriveUploadContext.Provider>;
};

export const useDriveUpload = () => {
    const context = useContext(DriveUploadContext);
    if(!context) {
        throw new Error("useDriveUpload must be used within a DriveUploadProvider");
    }
    return context;
};
export default DriveUploadProvider;
